import { useState, useContext, Fragment } from "react";
import { useStore } from "zustand";
import {
  Text,
  Heading,
  ActionButtonGroup,
  ActionButton,
  FileTrigger,
  Content,
  ContextualHelp,
  Footer,
  ColorSwatch,
  ColorSwatchPicker,
  DialogTrigger,
  Popover,
  Divider,
  TooltipTrigger,
  Tooltip,
} from "@react-spectrum/s2";
import { useHover } from "@react-aria/interactions";
import { useHasRole } from "@app/hooks/useRoles";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { store } from "@app/store";
import { SyntaxHighlighter } from "@app/components/SyntaxHighlighter";
import { S3Video } from "@app/components/S3Video";
import { S3Image } from "@app/components/S3Image";
import { EditPrompt } from "../EditPrompt";
import { FootageDialog } from "./FootageDialog";
import { Step, stepFooterStyle, stepFooterPromptStyles } from "./Step";
import FilmstripIllustration from "@react-spectrum/s2/illustrations/linear/Filmstrip";
import UploadToCloudIcon from "@react-spectrum/s2/icons/UploadToCloud";
import FilmstripIcon from "@react-spectrum/s2/icons/Filmstrip";
import CloseIcon from "@react-spectrum/s2/icons/Close";
import UndoIcon from "@react-spectrum/s2/icons/Undo";
import RedoIcon from "@react-spectrum/s2/icons/Redo";

import {
  style,
  lightDark,
  size,
  iconStyle,
} from "@react-spectrum/s2/style" with { type: "macro" };

export { sceneKey } from "@app/hooks/sceneGeneration";

export const ScenePreview = ({ scene, index, isDisabled }) => {
  const { addToast } = useStore(store, (state) => state);

  const { hoverProps, isHovered } = useHover({});
  const { selectedCampaign, video, editCampaign, handleUploadAssets } =
    useContext(VideoWizardContext);

  const isVideoEditor = useHasRole("video-editor");

  const [...availableColors] = new Set(
    [
      ...video.data?.assets
        ?.filter((a) => a.type === "color")
        .map((a) => a.color),
      "#FFFFFF",
      "#000000",
      "#EB1000",
    ].filter(Boolean),
  );

  const handleVideoUpload = async (files) => {
    if (isDisabled) {
      return;
    }

    const [file] = files;

    if (!file) {
      console.warn("No file selected for upload");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      console.warn("File size exceeds 20MB limit", file.size);
      addToast("File size exceeds 20MB limit", {
        variant: "negative",
        timeout: 1000 * 60,
      });
      return;
    }

    const { assets = [] } = await handleUploadAssets([file]);
    const [asset] = assets;

    if (!asset?.filePath) {
      console.warn("Failed to upload video asset", asset);
      return;
    }

    editCampaign.mutate({
      ...selectedCampaign,
      scenes: selectedCampaign?.scenes?.map((s, idx) =>
        idx === index - 1
          ? { ...s, Video: [asset?.filePath], Color: ["#000000"] }
          : s,
      ),
    });
  };

  const handleVideoSelect = (footage) => {
    if (isDisabled) {
      return;
    }

    if (!footage?.fileName) {
      return;
    }

    editCampaign.mutate({
      ...selectedCampaign,
      scenes: selectedCampaign?.scenes?.map((s, idx) =>
        idx === index - 1
          ? { ...s, Video: [footage.fileName], Color: ["#000000"] }
          : s,
      ),
    });
  };

  const handleColorChange = (color) => {
    if (isDisabled) {
      return;
    }

    editCampaign.mutate({
      ...selectedCampaign,
      scenes: selectedCampaign?.scenes?.map((s, idx) =>
        idx === index - 1 ? { ...s, Color: [color.toString("hex")] } : s,
      ),
    });
  };

  return (
    <div
      {...hoverProps}
      className={style({
        overflow: "hidden",
        display: "flex",
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        width: "full",
        aspectRatio: "16 / 9",
        backgroundColor: "layer-1",
        borderRadius: "default",
        boxShadow: "elevated",
        textAlign: "center",
        position: "relative",
        zIndex: 0,
      })}
      style={
        scene?.Color?.[0]
          ? {
              backgroundColor: scene?.Color?.[0],
              color: `contrast-color(color-mix(in srgb, ${scene?.Color?.[0]}, black 1%))`,
            }
          : {}
      }
    >
      {!!scene?.Video?.length && (
        <S3Video
          filePath={scene?.Video?.[0]}
          autoPlay
          loop
          muted
          playsInline
          className={style({
            size: "full",
            objectFit: "cover",
            filter: "[contrast(1.1) brightness(0.35)]",
            position: "absolute",
            inset: 0,
            zIndex: -1,
            pointerEvents: "none",
          })}
        />
      )}

      {!!scene?.Image?.length && (
        <div
          className={style({
            size: "full",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
          })}
        >
          {scene?.Image?.map((imagePath, idx) => (
            <Fragment key={idx}>
              {idx > 0 && (
                <CloseIcon styles={iconStyle({ color: "gray", size: "L" })} />
              )}
              <S3Image
                filePath={imagePath}
                styles={style({
                  backgroundColor: "transparent",
                  width: "25%",
                  height: "auto",
                  aspectRatio: "8 / 5",
                  objectFit: "contain",
                  objectPosition: {
                    isFirst: "right",
                    isLast: "left",
                    default: "center",
                  },
                })({
                  isFirst: idx === 0,
                  isLast: idx === scene?.Image?.length - 1,
                })}
              />
            </Fragment>
          ))}
        </div>
      )}

      <Text styles={style({ font: "heading", color: "inherit" })}>
        {scene?.Text}
      </Text>
      <div
        className={style({
          transition: "colors",
          position: "absolute",
          inset: 0,
          size: "full",
          backgroundColor: {
            isHovered: lightDark("white/65", "black/65"),
            default: "transparent",
          },
        })({ isHovered })}
      />
      {!isDisabled && (
        <ActionButtonGroup
          staticColor="auto"
          styles={style({
            position: "absolute",
            top: 8,
            insetEnd: 12,
          })}
          UNSAFE_className={style({
            transition: "opacity",
            opacity: {
              isHovered: 1,
              default: 0,
            },
          })({ isHovered })}
        >
          <DialogTrigger>
            <ActionButton aria-label="Pick background color">
              <ColorSwatch
                slot="icon"
                color={scene?.Color?.[0] ?? "#000000"}
                size="S"
              />
            </ActionButton>
            <Popover>
              <div
                className={style({
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  padding: 8,
                })}
              >
                <Text styles={style({ font: "detail" })}>
                  Pick a background color
                </Text>
                <ColorSwatchPicker
                  defaultValue={scene?.Color?.[0] ?? "#000000"}
                  rounding="default"
                  onChange={handleColorChange}
                >
                  {availableColors.map((color) => (
                    <ColorSwatch key={color} color={color} />
                  ))}
                </ColorSwatchPicker>
              </div>
            </Popover>
          </DialogTrigger>
          {isVideoEditor && (
            <DialogTrigger>
              <ActionButton aria-label="Change scene footage">
                <FilmstripIcon />
              </ActionButton>
              <FootageDialog
                defaultSelectedFilePath={scene?.Video?.[0]}
                onSelect={handleVideoSelect}
              />
            </DialogTrigger>
          )}
          <Divider
            orientation="vertical"
            size="S"
            styles={style({ marginX: 8, marginY: 4 })}
          />
          <FileTrigger
            acceptedFileTypes={["video/mp4"]}
            onSelect={handleVideoUpload}
          >
            <ActionButton aria-label="Upload video">
              <UploadToCloudIcon />
            </ActionButton>
          </FileTrigger>
          <ContextualHelp styles={style({ alignSelf: "center" })}>
            <Heading>Upload a video background</Heading>
            <Content>
              Replaces the current scene&apos;s background with your video.
              <br />
              Only the first 2 seconds will be used.
            </Content>
            <Footer>
              <Text>
                <Text styles={style({ font: "detail-sm" })}>Max size:</Text>
                <Text> 20 MB · </Text>
                <Text styles={style({ font: "detail-sm" })}>Format:</Text>
                <Text> MP4 only</Text>
              </Text>
            </Footer>
          </ContextualHelp>
        </ActionButtonGroup>
      )}
      <Text
        styles={style({
          opacity: { default: 0.25, isHovered: 0.75 },
          font: "ui-xl",
          transition: "colors",
          color: { default: "inherit", isHovered: lightDark("black", "white") },
          position: "absolute",
          top: 8,
          insetStart: 12,
        })({ isHovered })}
      >
        {index}
      </Text>
    </div>
  );
};

export const ScenesStep = ({ onContinue, onBack, continueLabel }) => {
  const {
    isStepPending,
    videoId,
    templateId,
    video,
    updateVideo,
    selectedCampaign,
    generateScenes,
    regenerateScenes,
    getScenesPromptTemplate,
  } = useContext(VideoWizardContext);

  if (!videoId) {
    console.warn(
      "ScenesStep component needs to be used within a VideoWizardContext provider",
    );
    return null;
  }

  const customerLogo = video.data?.assets?.find(
    (a) => a.name === "Logo",
  )?.filePath;

  const logotypes = [
    "public/adobe-wordmark-red-resize.svg",
    customerLogo,
  ].filter(Boolean);

  const [isScriptVisible, setIsScriptVisible] = useState(false);

  const isNextDisabled =
    generateScenes.isPending ||
    !selectedCampaign?.scenes ||
    selectedCampaign?.scenes?.length === 0;

  const isGenerating = generateScenes.isPending;

  const historicalCampaigns = video.data?.campaigns?.filter(
    (c) => !!c?.scenes?.length,
  );
  const hasHistoricalCampaigns = historicalCampaigns?.length > 1;
  const currentCampaignIndex = historicalCampaigns?.findIndex(
    (c) => c.id === selectedCampaign?.id,
  );
  const hasPreviousCampaign = currentCampaignIndex > 0;
  const hasNextCampaign =
    currentCampaignIndex < historicalCampaigns?.length - 1;

  const handleRegenerate = () => {
    const updatedCampaigns = [...video.data?.campaigns];
    const id = crypto.randomUUID();
    const updatedCampaign = {
      ...selectedCampaign,
      id,
      scenes: [],
    };

    updatedCampaigns.push(updatedCampaign);

    updateVideo.mutate(
      {
        campaigns: updatedCampaigns.slice(-10),
        selectedCampaignIds: [id],
      },
      {
        onSuccess: () => regenerateScenes(),
      },
    );
  };

  const handleUndo = () => {
    const previousCampaignIndex =
      historicalCampaigns?.findIndex((c) => c.id === selectedCampaign?.id) - 1;
    if (previousCampaignIndex >= 0) {
      const previousCampaign = historicalCampaigns?.[previousCampaignIndex];
      updateVideo.mutate({
        selectedCampaignIds: [previousCampaign.id],
      });
    }
  };

  const handleRedo = () => {
    const nextCampaignIndex =
      historicalCampaigns?.findIndex((c) => c.id === selectedCampaign?.id) + 1;
    if (nextCampaignIndex < historicalCampaigns?.length) {
      const nextCampaign = historicalCampaigns?.[nextCampaignIndex];
      updateVideo.mutate({
        selectedCampaignIds: [nextCampaign.id],
      });
    }
  };

  if (isStepPending) {
    return (
      <Step.Loading
        title="Preparing your scenes"
        subtitle="This may take a few moments, so please keep this tab open."
        ariaLabel="Generating campaign details"
        icon={<FilmstripIllustration />}
      />
    );
  }

  if (!selectedCampaign?.scenes?.length && generateScenes.isError) {
    return (
      <Step.Error
        title="Failed to load scenes"
        onRetry={() => regenerateScenes()}
      >
        There was an error generating your scenes. Please try again.
      </Step.Error>
    );
  }

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "flex",
          flexDirection: "column",
          gap: 32,
          size: "full",
          marginX: "auto",
          marginBottom: 64,
        })}
      >
        {!!selectedCampaign?.scenes?.length && (
          <>
            <Step.Header
              title="Review generated scenes"
              subtitle="You can adjust the details before moving to the next step."
            />

            <div
              className={style({
                size: "full",
                display: "flex",
                gap: 24,
                flexDirection: "column",
                alignItems: "start",
                opacity: {
                  isGenerating: 0.5,
                  default: 1,
                },
                filter: {
                  isGenerating: "[blur(1px)]",
                  default: "none",
                },
                willChange: "[opacity, filter]",
                pointerEvents: {
                  isGenerating: "none",
                  default: "auto",
                },
              })({ isGenerating })}
            >
              <div
                className={style({
                  width: "full",
                  display: "grid",
                  gridTemplateColumns: `repeat(auto-fill, minmax(${size(400)}, 1fr))`,
                  justifyContent: "center",
                  gap: 24,
                  alignItems: "start",
                })}
              >
                <ScenePreview
                  scene={{
                    Color: ["#ffffff"],
                    Image: logotypes,
                  }}
                  index="Intro"
                  isDisabled
                />

                {selectedCampaign?.scenes?.map((scene, idx) => (
                  <ScenePreview key={idx} scene={scene} index={idx + 1} />
                ))}

                <ScenePreview
                  scene={{
                    Color: ["#ffffff"],
                    Image: logotypes,
                  }}
                  index="Outro"
                  isDisabled
                />
              </div>
            </div>

            {isScriptVisible && (
              <SyntaxHighlighter
                code={JSON.stringify(selectedCampaign?.scenes ?? [], null, 2)}
              />
            )}
          </>
        )}
      </Step>

      {!!selectedCampaign?.scenes?.length && (
        <Step.Footer>
          <div className={stepFooterStyle}>
            <EditPrompt
              videoId={videoId}
              placeholder="Adjust scenes details"
              completions={selectedCampaign?.scenesPromptHints}
              getPromptTemplate={getScenesPromptTemplate}
              templateKey={`customTemplate__${templateId}-scenes`}
              onSubmit={regenerateScenes}
              onReset={generateScenes.reset}
              onRedo={handleRegenerate}
              isPending={generateScenes.isPending}
              styles={stepFooterPromptStyles}
              actions={
                hasHistoricalCampaigns && (
                  <>
                    <TooltipTrigger>
                      <ActionButton
                        onPress={handleUndo}
                        isDisabled={
                          updateVideo.isPending || !hasPreviousCampaign
                        }
                        aria-label="Undo"
                      >
                        <UndoIcon />
                      </ActionButton>
                      <Tooltip>Undo last changes</Tooltip>
                    </TooltipTrigger>
                    <TooltipTrigger>
                      <ActionButton
                        onPress={handleRedo}
                        isDisabled={updateVideo.isPending || !hasNextCampaign}
                        aria-label="Redo"
                      >
                        <RedoIcon />
                      </ActionButton>
                      <Tooltip>Revert changes</Tooltip>
                    </TooltipTrigger>
                  </>
                )
              }
            />
          </div>
        </Step.Footer>
      )}

      <Step.Actions
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={continueLabel}
        continueVariant="primary"
        isContinueDisabled={isNextDisabled}
      />
    </>
  );
};
